import { Circle } from "@mui/icons-material";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";
import theme from "../../App/Styles/theme";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";
import { IPerformanceReport } from "../../App/Typings/Reports/CampaignEmailPerformanceReport";
import { customLegendStyles } from "../CandidatesAddedByCampaign/CandidatesAddedByCampaignReport";
import { DiagonalXAxisTick } from "../Components/DiagonalXAxisTick";
import ReportContainer from "../Components/ReportContainer";

export const middleLineColor = "#556EC5";

const CustomLegend = (props: any) => {
  return (
    <Stack direction="row" alignItems="center" columnGap={2} rowGap={1} flexWrap="wrap">
      {props.payload.map((entry: any, index: number) => {
        return (
          <Box
            id={`campaignemailperformance-legenditem-${index}`}
            key={`campaignemailperformance-legenditem-${index}`}
          >
            <Stack title={entry.value} direction="row" alignItems="center" gap={0.5}>
              <Circle sx={{ color: entry.color }} fontSize="small" />
              <Typography color={entry.color} key={`item-${index}`} fontWeight="bold" sx={customLegendStyles}>
                {entry.value}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const CustomToolTip = (props: any) => {
  const { enableEmailsDeliveredAnalytics } = useFlags<IFeatureToggles>();

  const { id, payload, active } = props;
  if (!active || !payload?.length) {
    return null;
  }

  const dateAdded = payload[0].payload.dateAdded;
  const deliveredCount = payload.find((entry: any) => entry.dataKey === "deliveredCount")?.value ?? 0;
  const openCount = payload.find((entry: any) => entry.dataKey === "openCount")?.value ?? 0;
  const clickCount = payload.find((entry: any) => entry.dataKey === "clickCount")?.value ?? 0;
  const unsubscribeCount = payload.find((entry: any) => entry.dataKey === "unsubscribeCount")?.value ?? 0;

  return (
    <Card>
      <CardContent
        sx={{
          ".MuiTypography-root": { textAlign: "left", lineHeight: 1, pb: 1 },
          "&:last-child": { py: 1 },
        }}
      >
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}emailperformance-tooltip-unsubscribes-${dateAdded}`}
        >
          Unsubscribes: <span style={{ fontWeight: "bold" }}>{unsubscribeCount}</span>
        </Typography>
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}emailperformance-tooltip-clicks-${dateAdded}`}
        >
          Clicks: <span style={{ fontWeight: "bold" }}>{clickCount}</span>
        </Typography>
        <Typography
          sx={{ fontWeight: "normal" }}
          data-test={`${id}emailperformance-tooltip-opens-${dateAdded}`}
        >
          Unique Opens: <span style={{ fontWeight: "bold" }}>{openCount}</span>
        </Typography>
        {enableEmailsDeliveredAnalytics && (
          <Typography
            sx={{ fontWeight: "normal" }}
            data-test={`${id}emailperformance-tooltip-delivered-${dateAdded}`}
          >
            Delivered: <span style={{ fontWeight: "bold" }}>{deliveredCount}</span>
          </Typography>
        )}
        <Typography fontWeight={400} color={(theme) => theme.palette.text.secondary} sx={{ mb: 0, mt: 1 }}>
          {dateAdded}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const EmailPerformanceReport = ({
  id,
  reportData,
  loading,
  enabled,
}: {
  id: string;
  reportData: IPerformanceReport | undefined;
  loading: boolean;
  enabled: boolean;
}) => {
  const { enableEmailsDeliveredAnalytics } = useFlags<IFeatureToggles>();

  if (!enabled) {
    return null;
  }

  return (
    <ReportContainer id={`${id}-email-performance`} title="Email Performance Per Day" isLoading={loading}>
      <AreaChart
        data={reportData?.dataPoints}
        style={{
          position: "relative",
        }}
        margin={{
          top: 0,
          right: 30,
          left: 20,
          bottom: 60,
        }}
      >
        <defs>
          {enableEmailsDeliveredAnalytics && (
            // update color
            <linearGradient id="area-gradient-delivered" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.palette.motivatePrimary.main} stopOpacity={0.3} />
              <stop offset="95%" stopColor={theme.palette.motivatePrimary.main} stopOpacity={0} />
            </linearGradient>
          )}
          <linearGradient id="area-gradient-opens" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.palette.captivatePrimary.main} stopOpacity={0.3} />
            <stop offset="95%" stopColor={theme.palette.captivatePrimary.main} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="area-gradient-clicks" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={middleLineColor} stopOpacity={0.3} />
            <stop offset="95%" stopColor={middleLineColor} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="area-gradient-unsubs" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.palette.captivateSecondary.main} stopOpacity={0.3} />
            <stop offset="95%" stopColor={theme.palette.captivateSecondary.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#eeeded" vertical={false} />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey="dateAdded"
          tick={<DiagonalXAxisTick />}
          tickFormatter={(value: any) => new Date(value).toLocaleDateString()}
        />
        <YAxis axisLine={false} tickLine={false} type="number" domain={["auto", "dataMax"]} />
        <Tooltip
          content={<CustomToolTip id={id} />}
          cursor={{ stroke: theme.palette.secondary.main, strokeDasharray: 5 }}
        />
        <Legend
          layout="horizontal"
          verticalAlign="top"
          align="center"
          content={<CustomLegend />}
          payload={generateCustomLegendPayload(reportData, enableEmailsDeliveredAnalytics)}
          wrapperStyle={{ paddingBottom: 30 }}
        />
        <Area
          type="monotone"
          dataKey="unsubscribeCount"
          stroke={theme.palette.captivateSecondary.main}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-unsubs)"
          activeDot={{ r: 6 }}
        />
        <Area
          type="monotone"
          dataKey="clickCount"
          stroke={middleLineColor}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-clicks)"
          activeDot={{ r: 6 }}
        />
        <Area
          type="monotone"
          dataKey="openCount"
          stroke={theme.palette.captivatePrimary.main}
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#area-gradient-opens)"
          activeDot={{ r: 6 }}
        />
        {enableEmailsDeliveredAnalytics && (
          <Area
            type="monotone"
            dataKey="deliveredCount"
            stroke={theme.palette.motivatePrimary.main} // update color
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#area-gradient-delivered)"
          />
        )}
      </AreaChart>
    </ReportContainer>
  );
};

const generateCustomLegendPayload = (
  reportData?: IPerformanceReport,
  enableEmailsDeliveredAnalytics?: boolean,
) => {
  let totalDeliveredCount = 0;
  let totalOpensCount = 0;
  let totalClickCount = 0;
  let totalUnsubscribesCount = 0;

  for (const day of reportData?.dataPoints ?? []) {
    totalDeliveredCount += day.deliveredCount;
    totalOpensCount += day.openCount;
    totalClickCount += day.clickCount;
    totalUnsubscribesCount += day.unsubscribeCount;
  }

  const payload = [
    {
      dataKey: "openCount",
      color: theme.palette.captivatePrimary.main,
      value: "Unique Opens",
      totalCount: totalOpensCount,
    },
    {
      dataKey: "clickCount",
      color: middleLineColor,
      value: "Clicks",
      totalCount: totalClickCount,
    },
    {
      dataKey: "unsubscribeCount",
      color: theme.palette.captivateSecondary.main,
      value: "Unsubscribes",
      totalCount: totalUnsubscribesCount,
    },
  ];

  // Conditionally add the "Delivered" data if the feature toggle is enabled
  if (enableEmailsDeliveredAnalytics) {
    payload.unshift({
      dataKey: "deliveredCount",
      color: theme.palette.motivatePrimary.main, // update the color
      value: "Delivered",
      totalCount: totalDeliveredCount,
    });
  }

  return payload;
};
